import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { User } from '../types';
import { authAPI } from '../services/api';
import { loadUserLanguagePreference } from '../i18n';

interface AuthContextType {
  user: User | null;
  token: string | null;
  login: (email: string, password: string) => Promise<void>;
  register: (userData: {
    name: string;
    email: string;
    password: string;
    phone: string;
    role?: string;
  }) => Promise<void>;
  completeProfile: (phone: string, role?: string, skills?: string[], policeVerificationDocument?: string) => Promise<void>;
  updateUser: () => Promise<void>;
  refreshUser: () => Promise<void>;
  logout: () => void;
  loading: boolean;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Helper function to get current location
const getCurrentLocation = (): Promise<{ latitude: number; longitude: number; address: string } | null> => {
  return new Promise((resolve) => {
    if (!navigator.geolocation) {
      console.log('Geolocation is not supported by this browser');
      resolve(null);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          address: 'Current Location'
        });
      },
      (error) => {
        console.log('Error getting location:', error.message);
        resolve(null); // Don't fail login if location is not available
      },
      {
        timeout: 5000,
        maximumAge: 300000, // Cache for 5 minutes
        enableHighAccuracy: false // Faster, less battery drain
      }
    );
  });
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initAuth = async () => {
      try {
        // Check if localStorage is available (might not be in some incognito modes)
        let storedToken: string | null = null;
        let storedUser: string | null = null;
        
        try {
          storedToken = localStorage.getItem('token');
          storedUser = localStorage.getItem('user');
        } catch (e) {
          // localStorage might not be available in some incognito modes
          console.warn('localStorage not available:', e);
        }

        if (storedToken && storedUser) {
          setToken(storedToken);
          // Set user from localStorage first (for faster UI)
          try {
            const parsedUser = JSON.parse(storedUser);
            setUser(parsedUser);
            // Load language preference from stored user
            if ((parsedUser as any)?.languagePreference) {
              loadUserLanguagePreference((parsedUser as any).languagePreference);
            }
          } catch (e) {
            console.error('Failed to parse stored user:', e);
            setUser(null);
          }
          
          // Then fetch fresh profile data from API to ensure it's up to date
          try {
            const response = await authAPI.getProfile();
            const userData = response.data.user;
            setUser(userData);
            // Load user's language preference
            if ((userData as any)?.languagePreference) {
              loadUserLanguagePreference((userData as any).languagePreference);
            }
            try {
              localStorage.setItem('user', JSON.stringify(userData));
            } catch (e) {
              console.warn('Could not save user to localStorage:', e);
            }
          } catch (error) {
            console.error('Failed to fetch fresh profile:', error);
            // If token is invalid, clear everything
            setUser(null);
            setToken(null);
            try {
              localStorage.removeItem('token');
              localStorage.removeItem('user');
            } catch (e) {
              console.warn('Could not clear localStorage:', e);
            }
          }
        }

        // Check for Google OAuth token in URL
        const urlParams = new URLSearchParams(window.location.search);
        const oauthToken = urlParams.get('token');
        
        if (oauthToken) {
          // Store the token first
          setToken(oauthToken);
          try {
            localStorage.setItem('token', oauthToken);
          } catch (e) {
            console.warn('Could not save token to localStorage:', e);
          }
          
          // Remove token from URL immediately
          window.history.replaceState({}, document.title, window.location.pathname);
          
          // Fetch user profile to check if phone is missing BEFORE redirecting
          try {
            const response = await authAPI.getProfile();
            const userData = response.data.user;
            setUser(userData);
            // Load user's language preference
            if ((userData as any)?.languagePreference) {
              loadUserLanguagePreference((userData as any).languagePreference);
            }
            try {
              localStorage.setItem('user', JSON.stringify(userData));
            } catch (e) {
              console.warn('Could not save user to localStorage:', e);
            }
            
            // Try to update location after OAuth login (non-blocking)
            getCurrentLocation().then(location => {
              if (location) {
                authAPI.updateLocation(location).catch(err => {
                  console.log('Could not update location after OAuth:', err);
                });
              }
            });
            
            // Check if user needs to complete profile (missing phone) - redirect directly
            if (!userData.phone || userData.phone.trim() === '') {
              // Redirect directly to complete-profile, not via home
              window.location.replace('/complete-profile');
            } else {
              window.location.replace('/');
            }
          } catch (error) {
            console.error('Failed to fetch user profile:', error);
            setUser(null);
            setToken(null);
            try {
              localStorage.removeItem('token');
              localStorage.removeItem('user');
            } catch (e) {
              console.warn('Could not clear localStorage:', e);
            }
          }
        }
      } catch (error) {
        console.error('Error during auth initialization:', error);
      } finally {
        // Always set loading to false, even if there's an error
        setLoading(false);
      }
    };

    initAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = async (email: string, password: string) => {
    try {
      // Try to get current location (non-blocking)
      const location = await getCurrentLocation();
      
      // Login with or without location
      const response = await authAPI.login({ 
        email, 
        password,
        ...(location && { location })
      });
      const { user: userData, token: userToken } = response.data;

      setUser(userData);
      setToken(userToken);
      // Load user's language preference
      if ((userData as any)?.languagePreference) {
        loadUserLanguagePreference((userData as any).languagePreference);
      }
      localStorage.setItem('token', userToken);
      localStorage.setItem('user', JSON.stringify(userData));
      
      if (location) {
        console.log('📍 Location sent with login:', location);
      }
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Login failed');
    }
  };

  const register = async (userData: {
    name: string;
    email: string;
    password: string;
    phone: string;
    role?: string;
  }) => {
    try {
      const response = await authAPI.register(userData);
      const { user: newUser, token: userToken } = response.data;

      setUser(newUser);
      setToken(userToken);
      // Load user's language preference
      if ((newUser as any)?.languagePreference) {
        loadUserLanguagePreference((newUser as any).languagePreference);
      }
      localStorage.setItem('token', userToken);
      localStorage.setItem('user', JSON.stringify(newUser));
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Registration failed');
    }
  };

  const completeProfile = async (phone: string, role?: string, skills?: string[], policeVerificationDocument?: string) => {
    try {
      const profileData: any = { phone };
      if (role) {
        profileData.role = role;
      }
      if (skills && skills.length > 0) {
        profileData.skills = skills;
      }
      if (policeVerificationDocument) {
        profileData.policeVerificationDocument = policeVerificationDocument;
      }
      
      const response = await authAPI.completeProfile(profileData);
      const { user: userData, token: userToken } = response.data;

      // Update token if provided (new token from backend)
      if (userToken) {
        setToken(userToken);
        localStorage.setItem('token', userToken);
      }

      setUser(userData);
      // Load user's language preference
      if ((userData as any)?.languagePreference) {
        loadUserLanguagePreference((userData as any).languagePreference);
      }
      localStorage.setItem('user', JSON.stringify(userData));
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Profile completion failed');
    }
  };

  const updateUser = useCallback(async () => {
    try {
      const response = await authAPI.getProfile();
      const userData = response.data.user;
      setUser(userData);
      // Load user's language preference
      if ((userData as any)?.languagePreference) {
        loadUserLanguagePreference((userData as any).languagePreference);
      }
      localStorage.setItem('user', JSON.stringify(userData));
    } catch (error: any) {
      console.error('Failed to update user:', error);
      throw new Error(error.response?.data?.message || 'Failed to update user');
    }
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      const response = await authAPI.getProfile();
      const userData = response.data.user;
      setUser(userData);
      // Load user's language preference
      if ((userData as any)?.languagePreference) {
        loadUserLanguagePreference((userData as any).languagePreference);
      }
      localStorage.setItem('user', JSON.stringify(userData));
    } catch (error: any) {
      console.error('Failed to refresh user:', error);
      // Don't throw error, just log it
    }
  }, []);

  const logout = () => {
    setUser(null);
    setToken(null);
    localStorage.removeItem('token');
    localStorage.removeItem('user');
  };

  const value: AuthContextType = {
    user,
    token,
    login,
    register,
    completeProfile,
    updateUser,
    refreshUser,
    logout,
    loading,
    isAuthenticated: !!user && !!token,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
